import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { dropdownVariants } from '../../utils/animations';
import { SkeletonListItem } from '../ui';
import { Empty, PageHeader, Panel, Pill } from '../ui/kit';
import { MARKS } from '../../lib/marks';

interface ActivityLog {
  id: string;
  userId: string;
  action: string;
  entity: string;
  entityId: string | null;
  details: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  description: string;
}

// Action icons
const getActionIcon = (action: string) => {
  switch (action) {
    case 'login':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
        </svg>
      );
    case 'logout':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15m-3 0-3-3m0 0 3-3m-3 3H15" />
        </svg>
      );
    case 'create':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
      );
    case 'update':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Z" />
        </svg>
      );
    case 'delete':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
        </svg>
      );
    case 'export':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
        </svg>
      );
    default:
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
        </svg>
      );
  }
};

// Action colors
const getActionColor = (action: string) => {
  switch (action) {
    case 'login':
      return 'bg-zinc-100 text-zinc-600';
    case 'logout':
      return 'bg-zinc-100 text-zinc-500';
    case 'create':
      return 'bg-zinc-100 text-zinc-600';
    case 'update':
      return 'bg-zinc-200 text-zinc-800';
    case 'delete':
      return 'bg-red-100 text-red-600';
    case 'export':
      return 'bg-zinc-100 text-zinc-600';
    default:
      return 'bg-zinc-100 text-zinc-500';
  }
};

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInMinutes < 1) return 'just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInHours < 24) return `${diffInHours}h ago`;
  if (diffInDays === 1) return 'yesterday';
  if (diffInDays <= 6) return `${diffInDays} days ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFullDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ActivityPage: React.FC = () => {
  const [filterAction, setFilterAction] = useState<string>('');
  const [page, setPage] = useState(0);
  const limit = 20;

  // Fetch activity logs
  const { data, isLoading, error } = useQuery({
    queryKey: ['activity-logs', filterAction, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterAction) params.append('action', filterAction);
      params.append('limit', String(limit));
      params.append('offset', String(page * limit));
      const response = await axios.get(`/api/activity?${params.toString()}`);
      return response.data.data;
    },
    placeholderData: (previousData) => previousData,
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6">
        <h3 className="font-semibold text-red-900">Error loading activity</h3>
        <p className="mt-1 text-[15px] text-red-700">Unable to load activity log. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Account"
        title="Activity"
        description="Track your recent actions and account activity"
      />

      {/* Filter */}
      <div className="flex flex-wrap gap-2">
        <FilterDropdown
          value={filterAction}
          onChange={(value) => {
            setFilterAction(value);
            setPage(0);
          }}
          options={[
            { value: '', label: 'All Actions' },
            { value: 'login', label: 'Logins' },
            { value: 'logout', label: 'Logouts' },
            { value: 'create', label: 'Creates' },
            { value: 'update', label: 'Updates' },
            { value: 'delete', label: 'Deletes' },
            { value: 'export', label: 'Exports' },
          ]}
        />
        <AnimatePresence>
          {filterAction && (
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={() => {
                setFilterAction('');
                setPage(0);
              }}
              className="rounded-full px-3 py-2 text-[13px] text-muted transition-colors hover:text-primary"
            >
              Clear filter
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Activity List */}
      <div>
        {isLoading && !data ? (
          <Panel className="overflow-hidden">
            <div className="divide-y divide-zinc-100">
              {[...Array(5)].map((_, i) => (
                <SkeletonListItem key={i} />
              ))}
            </div>
          </Panel>
        ) : logs.length === 0 ? (
          <Empty
            mark={MARKS.activity}
            eyebrow="History"
            title="No activity yet"
            description="Every change you or we make to your site is logged here with a timestamp."
          />
        ) : (
          <Panel className="overflow-hidden">
            <div className="divide-y divide-zinc-100">
              {logs.map((log: ActivityLog) => (
                <ActivityRow key={log.id} log={log} />
              ))}
            </div>
          </Panel>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
            Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total} activities
          </p>
          <div className="flex gap-2">
            <Pill
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              tone="outline"
              size="sm"
            >
              Previous
            </Pill>
            <Pill
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page >= totalPages - 1}
              tone="outline"
              size="sm"
            >
              Next
            </Pill>
          </div>
        </div>
      )}
    </div>
  );
};

// Activity Row Component
interface ActivityRowProps {
  log: ActivityLog;
}

const ActivityRow: React.FC<ActivityRowProps> = ({ log }) => {
  return (
    <div className="p-4 transition-colors hover:bg-paper-tint">
      <div className="flex items-center gap-4">
        {/* Icon */}
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
          {getActionIcon(log.action)}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-medium text-primary">{log.description}</p>
          <div className="mt-0.5 flex items-center gap-3 text-[13px] text-muted">
            <span className="capitalize">{log.entity}</span>
            {log.entityId && (
              <span className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[11px]">
                {log.entityId.slice(0, 8)}...
              </span>
            )}
          </div>
        </div>

        {/* Time */}
        <div className="text-right flex-shrink-0">
          <p className="font-mono text-[13px] text-muted">{formatRelativeDate(log.createdAt)}</p>
          <p className="hidden font-mono text-[11px] text-muted/70 sm:block">{formatFullDate(log.createdAt)}</p>
        </div>
      </div>
    </div>
  );
};

// Filter Dropdown Component
interface FilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value) || options[0];
  const isFiltered = value !== '';

  return (
    <div className="relative">
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-[13px] font-medium transition-all
          ${isFiltered
            ? 'bg-primary text-white border-primary'
            : 'bg-white border-zinc-200 text-primary hover:bg-zinc-100'
          }
        `}
      >
        {selectedOption?.label}
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </motion.svg>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              variants={dropdownVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-xl z-20 min-w-[160px] overflow-hidden"
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors
                    ${option.value === value
                      ? 'bg-paper-tint text-primary font-medium'
                      : 'text-primary hover:bg-zinc-100'
                    }
                  `}
                >
                  {option.label}
                </button>
              ))}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityPage;
